import { ElementRef } from "@angular/core";
import { LayoutVars } from "@app/store/session/session.store";

export class MarketPlacePersonalizationUtils {

    static initCssVars(elRef: ElementRef, layoutVars: LayoutVars): void {
        if (layoutVars) {
            this.setCssVar(elRef, '--primary-color', layoutVars.primaryColor);
            this.setCssVar(elRef, '--shelf-title-color', layoutVars.primaryColor);
            this.setCssVar(elRef, '--primary-color-alt', MarketPlacePersonalizationUtils.getColorNotWhite(['primaryColor', 'offerColor', 'buttonColor'], layoutVars, '#16b097'));
            this.setCssVar(elRef, '--secondary-button-color', layoutVars.buttonColor);
            this.setCssVar(elRef, '--secondary-color-orange', layoutVars.buttonColor);
            this.setCssVar(elRef, '--button-focus-box-shadow', '0 0 0 3px rgba(var(--secondary-button-color), 0.5)');
            this.setCssVar(elRef, '--button-hover-color', layoutVars.buttonHoverColor);
            this.setCssVar(elRef, '--title-color', layoutVars.offerColor);
            this.setCssVar(elRef, '--title-color-alt', MarketPlacePersonalizationUtils.getColorNotWhite(['offerColor', 'primaryColor', 'buttonColor'], layoutVars, '#1c2e42'));
            this.setCssVar(elRef, '--logo-url', layoutVars.logoUrl ? `url("${layoutVars.logoUrl}")` : undefined);
            this.setCssVar(elRef, '--login-bg-image-url', layoutVars.loginBgImageUrl ? `url("${layoutVars.loginBgImageUrl}")` : undefined);
            this.setCssVar(elRef, '--share-bg-image-url', layoutVars.loginBgImageUrl ? `url("${layoutVars.loginBgImageUrl}")` : undefined);
            this.setCssVar(elRef, '--menu-item-color', layoutVars.menuItemColor);
            this.setCssVar(elRef, '--menu-item-color-alt', MarketPlacePersonalizationUtils.getColorNotWhite(['menuItemColor', 'offerColor', 'primaryColor', 'buttonColor'], layoutVars, '#1c2e42'));
            this.setCssVar(elRef, '--menu-item-color-hover', layoutVars.menuItemColorHover);

            // Contraste de texto sobre superfícies com --title-color (evita branco-sobre-branco)
            const onTitle = MarketPlacePersonalizationUtils.getOnTitleColor(layoutVars);
            this.setCssVar(elRef, '--on-title-color', onTitle.text);
            this.setCssVar(elRef, '--on-title-muted', onTitle.muted);
            this.setCssVar(elRef, '--on-title-soft', onTitle.soft);
            this.setCssVar(elRef, '--on-title-divider', onTitle.divider);
            this.setCssVar(elRef, '--on-title-grid', onTitle.grid);

            //footer
            this.setCssVar(elRef, '--link-footer-title', layoutVars.menuItemColor);
            this.setCssVar(elRef, '--social-media', layoutVars.menuItemColor);
        }
    }


    static setCssVar(elRef: ElementRef, varName: string, value: string) {
        if (value) {
            elRef?.nativeElement?.style?.setProperty(varName, value);
            const doc = elRef?.nativeElement?.ownerDocument;
            const root = doc?.documentElement;
            const body = doc?.body;
            root?.style?.setProperty(varName, value);
            body?.style?.setProperty(varName, value);
            const overlay = doc?.querySelector?.('.cdk-overlay-container') as HTMLElement | null;
            overlay?.style?.setProperty(varName, value);
        }
    }

    static isWhite(color: string): boolean {
        if (!color) {
            return false;
        }

        const whiteColors = ['#fff', '#ffffff', 'white', 'rgb(255, 255, 255)'];
        if (whiteColors.includes(color.toLowerCase())) {
            return true;
        }

        const hexToRgb = (hex: string): [number, number, number] | null => {
            hex = hex.replace(/^#/, '');
            if (hex.length === 3) {
                hex = hex.split('').map(char => char + char).join('');
            }
            if (hex.length !== 6) {
                return null;
            }
            const bigint = parseInt(hex, 16);
            return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
        };

        const rgbStringToArray = (rgb: string): [number, number, number] | null => {
            const match = rgb.match(/(\d+),\s*(\d+),\s*(\d+)/);
            return match ? [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])] : null;
        };

        let rgb: [number, number, number] | null = null;

        if (color.startsWith('#')) {
            rgb = hexToRgb(color);
        } else if (color.startsWith('rgb')) {
            rgb = rgbStringToArray(color);
        }

        if (!rgb) {
            return false;
        }

        const [r, g, b] = rgb;
        
        const threshold = 245;
        return r >= threshold && g >= threshold && b >= threshold;
    }

    static getColorNotWhite(fields: string[], layoutVars: LayoutVars, fallback = ''): string {
        if (layoutVars) {
            for (const field of fields) {
                const color = layoutVars[field];
                if (color && !this.isWhite(color)) {
                    return color;
                }
            }
        }
        return fallback;
    }

    // Cores de texto/divisores para conteúdo sobre --title-color.
    // Quando title/offer é branco (ou quase), inverte para paleta escura.
    static getOnTitleColor(layoutVars: LayoutVars): {
        text: string;
        muted: string;
        soft: string;
        divider: string;
        grid: string;
    } {
        const titleColor = layoutVars?.offerColor;
        if (this.isWhite(titleColor)) {
            return {
                text: this.getColorNotWhite(['menuItemColor', 'primaryColor', 'buttonColor'], layoutVars, '#1c2e42'),
                muted: '#6b7787',
                soft: '#42526e',
                divider: 'rgba(28, 46, 66, 0.12)',
                grid: 'rgba(28, 46, 66, 0.1)',
            };
        }

        const menuColor = layoutVars?.menuItemColor;
        const text = menuColor && !this.isWhite(menuColor) ? menuColor : '#ffffff';

        return {
            text,
            muted: '#8b93a0',
            soft: '#cfd5dc',
            divider: 'rgba(255, 255, 255, 0.08)',
            grid: 'rgba(255, 255, 255, 0.08)',
        };
    }
}